import NewsFallbackIcon from '@images/icons/news-fallback-icon.svg?react'
import cn from 'classnames'
import { useMarkNewsAsSeenMutation, useNewsImageQry } from 'mmfintech-backend-api'
import type { PortalNewsResponse } from 'mmfintech-backend-api/types'
import { GlobalContext } from 'mmfintech-commons'
import { ErrorDisplay, Spinner } from 'mmfintech-portal-commons'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useState } from 'react'
import { NewsButtonsContainer, NewsContentSection } from './elements'
import styles from './NewsModal.module.scss'

export const NewsModal = ({ news }) => {
  const { modalHide } = useContext(GlobalContext)

  const [seen, setSeen] = useState(new Set<number>())
  const [currentIndex, setCurrentIndex] = useState<number>(0)

  const [loading, setLoading] = useState(true)

  const { image, loadImage } = useNewsImageQry()
  const [markAsSeen, { error }] = useMarkNewsAsSeenMutation()

  const markNewsAsSeen = async (newsId: number) => {
    if (!seen.has(newsId)) {
      try {
        await markAsSeen({ newsId }).unwrap()
        setSeen(prev => new Set(prev).add(newsId))
      } catch {}
    }
  }

  const handleTransition = (index: number): void => {
    if (index >= 0 && index < news.length) {
      void markNewsAsSeen(news[index].id)
      setCurrentIndex(index)
    } else if (index === news.length) {
      modalHide()
    }
  }

  useEffect(() => {
    if (news.length > 0) {
      void markNewsAsSeen(news[0].id)
    }
    return () => {
      void markNewsAsSeen(news[currentIndex].id)
    }
  }, [news, currentIndex])

  useEffect(() => {
    const data: PortalNewsResponse = news?.[currentIndex]
    const { id: newsId } = data || {}

    loadImage(newsId, onImageLoad)
  }, [currentIndex])

  const onImageLoad = () => {
    setLoading(false)
  }

  return (
    <div className={cn(styles['news-modal-wrapper'], 'news-modal-wrapper')}>
      {news?.[currentIndex].imagePath ? (
        loading ? (
          <Spinner />
        ) : (
          <div
            className={cn(styles['news-image'], 'news-image')}
            style={{ '--news-image-bg': `url(${image})` } as CSSProperties}
          />
        )
      ) : (
        <NewsFallbackIcon className='fallback-icon' />
      )}

      <NewsContentSection
        title={news?.[currentIndex].title}
        content={news?.[currentIndex].content}
        availableNews={news}
        activeNewsIndex={currentIndex}
        handleTransition={handleTransition}
      />

      <ErrorDisplay error={error} />

      <NewsButtonsContainer
        activeNewsIndex={currentIndex}
        availableNewsLength={news?.length}
        handleTransition={handleTransition}
      />
    </div>
  )
}
